import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import LandingArticle from '@/components/LandingArticle';

const InlineCTA = () => (
  <div className="my-6 not-prose rounded-2xl border border-emerald-700/15 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="inline-cta">
    <div className="text-sm text-emerald-900">
      <strong>Try BCM-95®.</strong>
      <span className="block text-emerald-800/80 mt-0.5">60 vegetarian capsules · UK Royal Mail 48. First-bottle welcome offer £37.99 all-in.</span>
    </div>
    <Link to="/products/94056720-f372-4eef-bcfe-74063137a6e5" className="inline-flex shrink-0 items-center gap-1.5 bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-sm hover:bg-emerald-800 transition-colors">
      Shop now <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

const sections = [
  {
    id: 'what-is',
    heading: 'What exactly is BCM-95®?',
    body: (
      <>
        <p><strong>BCM-95<sup>&reg;</sup></strong> — branded internationally as <strong>Curcugreen<sup>&reg;</sup></strong> — is a patented curcumin formulation co-developed by Dr Benny Antony, PhD, and Arjuna Natural. It combines a standardised curcuminoid fraction (~86% curcuminoids) with turmeric's own volatile essential oils (~7–9% turmerones), all sourced from <em>Curcuma longa</em> rhizome.</p>
        <p>The simple idea: rather than bolt on a foreign carrier (piperine, phospholipids, synthetic emulsifiers), use the turmeric plant's <em>own</em> oily fraction to keep curcumin in a solubilised, absorbable state through the gut. The result is a clean-label, single-plant extract.</p>
      </>
    ),
  },
  {
    id: 'difference',
    heading: 'How is BCM-95® different from standard curcumin?',
    body: (
      <>
        <p>"Standard" curcumin (often labelled as "95% curcumin extract") is a dry, powdered concentrate. It is highly potent in vitro but, in vivo, very poorly absorbed and very rapidly excreted. Reported relative bioavailability vs unformulated curcumin varies by study and methodology.</p>
        <p>BCM-95® differs in three ways:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>It is not just powder.</strong> The curcuminoids are reintegrated with turmeric essential oils — recreating, in a controlled and standardised way, something closer to the whole-root chemistry.</li>
          <li><strong>It has published pharmacokinetic data.</strong> Antony B et al., Indian J Pharm Sci 2008, reported substantially higher relative oral bioavailability vs standard curcumin in healthy volunteers (frequently cited in the curcumin literature as multiple-fold higher).</li>
          <li><strong>No piperine.</strong> Useful if you are concerned about CYP3A4-mediated drug interactions or simply want a single-plant extract.</li>
        </ul>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'inside-bottle',
    heading: 'What is inside a BCM-95® bottle?',
    body: (
      <>
        <p>BCM-95® by IIY Phytoventra delivers BCM-95® in a clean, allergen-free vegetable capsule:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>1000 mg</strong> of Purified Turmeric Extract Curcugreen<sup>&reg;</sup> per 2 capsules</li>
          <li>Of which <strong>650 mg curcumin</strong> (standardised)</li>
          <li>Plus the native turmeric oil fraction (turmerones) that defines the BCM-95® patent</li>
          <li>60 vegetarian capsules per bottle — a 30-day supply at the 2-cap daily serving</li>
          <li>Vegan · gluten-free · soya-free · dairy-free · no artificial colours</li>
          <li>Manufactured under GMP-certified conditions, using <strong>100% solar power</strong> at the source plant</li>
        </ul>
      </>
    ),
  },
  {
    id: 'science',
    heading: 'The science: what has been studied?',
    body: (
      <>
        <p>BCM-95® / Curcugreen has been the subject of dozens of peer-reviewed publications over more than 15 years. Without making medicinal claims (we are a food supplement under UK regulation), areas the research has explored include:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Relative oral bioavailability vs standard curcumin in healthy volunteers (PK studies)</li>
          <li>Tolerability over multi-week dosing</li>
          <li>Roles within active-living, mood-balance and joint-comfort routines</li>
        </ul>
        <p>Two specific references that IIY Phytoventra highlights on the Scientific Advisor section:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Lopresti AL, Maes M, Maker GL, Hood SD, Drummond PD. <em>Curcumin for the treatment of major depression: a randomised, double-blind, placebo-controlled study.</em> J Affect Disord. 2014 Oct;167:368–75.</li>
          <li>Cochrane Central Register entry, BCM-95® controlled trial — full record at the Cochrane Library.</li>
        </ul>
        <p className="text-[13px] italic text-slate-500">BCM-95® is a food supplement. It is not a medicine and is not intended to diagnose, treat, cure or prevent any disease.</p>
      </>
    ),
  },
  {
    id: 'how-to-take',
    heading: 'How to take BCM-95®',
    body: (
      <>
        <p>Take <strong>two capsules per day with food</strong> — typically one in the morning and one in the evening. Curcumin absorbs better when taken with a meal that contains a little fat (eggs, avocado, olive oil, oily fish, etc.).</p>
        <p>Most curcumin studies measure outcomes over 8–12 weeks of consistent daily use. As with all polyphenol-rich routines, regularity matters more than mega-dosing.</p>
      </>
    ),
  },
  {
    id: 'buy',
    heading: 'Where to buy in the UK',
    body: (
      <>
        <p>BCM-95® ships from the UK via Royal Mail 48 tracked delivery. Pricing is <strong>all-inclusive of UK VAT and standard delivery</strong> — no surprises at checkout:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>First-time buyer welcome offer: <strong>£37.99</strong> for a single bottle</li>
          <li>Standard single bottle: <strong>£44.99</strong></li>
          <li>Bundle pricing: 2 bottles £84.99 · 3 bottles £113.99 (Best Value)</li>
          <li>Wholesale: 101+ bottles, bespoke quotes from 301+</li>
        </ul>
        <p>VAT-registered businesses can request an itemised VAT invoice at checkout. A sequential Proforma PDF (`IIYPV/XXXXX`) is emailed automatically.</p>
        <p>Want the full consumer overview of the finished product (what&apos;s in a bottle, how to take it, where to buy)? Read our pillar guide: <Link to="/biocurcumax" className="text-emerald-700 underline font-semibold">BCM-95® UK Guide</Link>.</p>
      </>
    ),
  },
];

export default function BCM95Explained() {
  return (
    <LandingArticle
      title="BCM-95® Curcumin Explained: Patented Curcugreen® | IIY Phytoventra"
      description="What is BCM-95®? A plain-English breakdown of the patented Curcugreen® curcumin formulation, its turmeric-oil bioavailability approach, and how BCM-95® uses it."
      pathOverride="/bcm-95-explained"
      heading="BCM-95® Explained: The Patented Curcumin Formulation Behind BCM-95®"
      subheading="No piperine. No phospholipid carriers. Just standardised curcuminoids reintegrated with turmeric's own essential oils — a single-plant approach to one of the supplement world's hardest bioavailability problems."
      tag="BCM-95® Guide"
      datePublished="2026-02-10"
      sections={sections}
    />
  );
}
